import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SEOHead } from "@/components/SEOHead";
import { 
  Store, 
  ArrowRight, 
  ArrowLeft,
  CheckCircle,
  User,
  MapPin,
  CreditCard,
  Package,
  Palette,
  Globe,
  Camera,
  Star
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const SellerOnboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Store Info
    storeName: "",
    storeDescription: "",
    storeCategory: "",
    // Step 2: Business Info
    businessName: "",
    businessType: "",
    taxId: "",
    phoneNumber: "",
    // Step 3: Address
    address1: "",
    address2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "BS",
    // Step 4: Products
    productCategories: [],
    estimatedProducts: "",
    // Step 5: Payment
    paymentMethod: "",
    bankAccount: "",
    // Step 6: Launch
    launchGoal: "",
    experience: ""
  });

  const totalSteps = 6;
  const progress = (currentStep / totalSteps) * 100;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // TODO: Save onboarding data to database
      console.log("Submitting onboarding data:", formData);

      toast.success("Welcome to Bahama Marketplace! Your seller account is ready.");
      navigate("/seller");
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error("Failed to complete setup. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const StepHeader = ({ step, title, description }: { step: number; title: string; description: string }) => (
    <div className="text-center mb-8">
      <div className="flex items-center justify-center gap-4 mb-4">
        <Badge variant="secondary" className="px-3 py-1">
          Step {step} of {totalSteps}
        </Badge>
        <Progress value={progress} className="w-32" />
      </div>
      <h2 className="text-2xl font-bold mb-2">{title}</h2>
      <p className="text-muted-foreground">{description}</p>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <StepHeader 
              step={1} 
              title="Set up your store"
              description="Tell us about your store and what makes it unique"
            />
            <div className="space-y-4">
              <div>
                <Label htmlFor="storeName">Store Name *</Label>
                <Input
                  id="storeName"
                  value={formData.storeName}
                  onChange={(e) => updateFormData('storeName', e.target.value)}
                  placeholder="Your Amazing Store"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This is how customers will see your store
                </p>
              </div>

              <div>
                <Label htmlFor="storeDescription">Store Description *</Label>
                <Textarea
                  id="storeDescription"
                  value={formData.storeDescription}
                  onChange={(e) => updateFormData('storeDescription', e.target.value)}
                  placeholder="Describe what your store offers and what makes it special..."
                  rows={4}
                  required
                />
              </div>

              <div>
                <Label htmlFor="storeCategory">Store Category *</Label>
                <Select value={formData.storeCategory} onValueChange={(value) => updateFormData('storeCategory', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your primary category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fashion">Fashion & Apparel</SelectItem>
                    <SelectItem value="electronics">Electronics</SelectItem>
                    <SelectItem value="home">Home & Garden</SelectItem>
                    <SelectItem value="beauty">Beauty & Personal Care</SelectItem>
                    <SelectItem value="sports">Sports & Outdoors</SelectItem>
                    <SelectItem value="books">Books & Media</SelectItem>
                    <SelectItem value="jewelry">Jewelry & Accessories</SelectItem>
                    <SelectItem value="food">Food & Beverages</SelectItem>
                    <SelectItem value="art">Arts & Crafts</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <StepHeader 
              step={2} 
              title="Business information"
              description="Help us understand your business for tax and legal purposes"
            />
            <div className="space-y-4">
              <div>
                <Label htmlFor="businessName">Legal Business Name *</Label>
                <Input
                  id="businessName"
                  value={formData.businessName}
                  onChange={(e) => updateFormData('businessName', e.target.value)}
                  placeholder="Your Business LLC"
                  required
                />
              </div>

              <div>
                <Label htmlFor="businessType">Business Type *</Label>
                <Select value={formData.businessType} onValueChange={(value) => updateFormData('businessType', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual/Sole Proprietor</SelectItem>
                    <SelectItem value="llc">Limited Liability Company (LLC)</SelectItem>
                    <SelectItem value="corporation">Corporation</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="taxId">Tax ID / Business License Number</Label>
                <Input
                  id="taxId"
                  value={formData.taxId}
                  onChange={(e) => updateFormData('taxId', e.target.value)}
                  placeholder="Optional"
                />
              </div>

              <div>
                <Label htmlFor="phoneNumber">Business Phone Number *</Label>
                <Input
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) => updateFormData('phoneNumber', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  required
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <StepHeader 
              step={3} 
              title="Business address"
              description="Where is your business located? This helps with shipping and taxes"
            />
            <div className="space-y-4">
              <div>
                <Label htmlFor="address1">Street Address *</Label>
                <Input
                  id="address1"
                  value={formData.address1}
                  onChange={(e) => updateFormData('address1', e.target.value)}
                  placeholder="123 Business Street"
                  required
                />
              </div>

              <div>
                <Label htmlFor="address2">Address Line 2</Label>
                <Input
                  id="address2"
                  value={formData.address2}
                  onChange={(e) => updateFormData('address2', e.target.value)}
                  placeholder="Suite 100 (optional)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => updateFormData('city', e.target.value)}
                    placeholder="Nassau"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="state">State/Province *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => updateFormData('state', e.target.value)}
                    placeholder="New Providence"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="postalCode">Postal Code *</Label>
                  <Input
                    id="postalCode"
                    value={formData.postalCode}
                    onChange={(e) => updateFormData('postalCode', e.target.value)}
                    placeholder="00000"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Select value={formData.country} onValueChange={(value) => updateFormData('country', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BS">Bahamas</SelectItem>
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="CA">Canada</SelectItem>
                      <SelectItem value="GB">United Kingdom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <StepHeader 
              step={4} 
              title="Your products"
              description="Tell us about what you plan to sell"
            />
            <div className="space-y-4">
              <div>
                <Label>How many products do you plan to sell? *</Label>
                <Select value={formData.estimatedProducts} onValueChange={(value) => updateFormData('estimatedProducts', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10 products</SelectItem>
                    <SelectItem value="11-50">11-50 products</SelectItem>
                    <SelectItem value="51-100">51-100 products</SelectItem>
                    <SelectItem value="100+">More than 100 products</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Alert>
                <Package className="h-4 w-4" />
                <AlertDescription>
                  Don't worry if you're not sure yet. You can always add more products later. 
                  We'll help you set up your first few products to get started.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <StepHeader 
              step={5} 
              title="Payment setup"
              description="How would you like to receive payments from customers?"
            />
            <div className="space-y-4">
              <div>
                <Label>Payment Method *</Label>
                <Select value={formData.paymentMethod} onValueChange={(value) => updateFormData('paymentMethod', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank Transfer</SelectItem>
                    <SelectItem value="digital-wallet">Digital Wallet</SelectItem>
                    <SelectItem value="later">Set up later</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Alert>
                <CreditCard className="h-4 w-4" />
                <AlertDescription>
                  Payment setup can be completed later in your dashboard. 
                  We support various payment methods including bank transfers and digital wallets.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <StepHeader 
              step={6} 
              title="Ready to launch!"
              description="You're almost ready to start selling on Bahama Marketplace"
            />
            <div className="space-y-6">
              <div>
                <Label>What's your main goal for selling online? *</Label>
                <Select value={formData.launchGoal} onValueChange={(value) => updateFormData('launchGoal', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="side-income">Generate side income</SelectItem>
                    <SelectItem value="full-business">Build a full-time business</SelectItem>
                    <SelectItem value="expand">Expand existing business</SelectItem>
                    <SelectItem value="test-market">Test market demand</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>How experienced are you with online selling? *</Label>
                <Select value={formData.experience} onValueChange={(value) => updateFormData('experience', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your experience level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Complete beginner</SelectItem>
                    <SelectItem value="some">Some experience</SelectItem>
                    <SelectItem value="experienced">Very experienced</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-card border border-border/40 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  <h3 className="font-semibold">You're all set!</h3>
                </div>
                <p className="text-muted-foreground text-sm mb-4">
                  After completing setup, you'll be able to:
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <span>Add your first products</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-blue-500" />
                    <span>Customize your store appearance</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Camera className="h-4 w-4 text-green-500" />
                    <span>Upload photos and descriptions</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-purple-500" />
                    <span>Start receiving orders</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.storeName && formData.storeDescription && formData.storeCategory;
      case 2:
        return formData.businessName && formData.businessType && formData.phoneNumber;
      case 3:
        return formData.address1 && formData.city && formData.state && formData.postalCode;
      case 4:
        return formData.estimatedProducts;
      case 5:
        return formData.paymentMethod;
      case 6:
        return formData.launchGoal && formData.experience;
      default:
        return true;
    }
  };

  if (!user) {
    navigate("/seller/register");
    return null;
  }

  return (
    <>
      <SEOHead
        title="Complete Your Setup - Bahama Marketplace Seller"
        description="Complete your seller setup and start selling on Bahama Marketplace today."
      />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Store className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-3xl font-bold mb-2">Set up your seller account</h1>
              <p className="text-muted-foreground">
                Let's get your store ready to sell on Bahama Marketplace
              </p>
            </div>

            <Card className="shadow-xl border-border/40">
              <CardContent className="p-8">
                {renderStep()}
                <Separator className="my-8" />
                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                  {currentStep < totalSteps ? (
                    <Button
                      onClick={handleNext}
                      disabled={!canProceed()}
                      className="flex items-center gap-2"
                    >
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={!canProceed() || loading}
                      className="flex items-center gap-2"
                    >
                      {loading ? "Setting up..." : "Complete Setup"}
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default SellerOnboarding;